/**
 * ReportHandoff — chat-to-report action UI, rendered inside the chat.
 *
 *   - single resolved client  -> one "Open <report> for <client>" button
 *   - ambiguous reference      -> a Claude-Code-style choice card: a single
 *                                 panel listing every matching client plus an
 *                                 inline "Other" text field. Picking a choice
 *                                 COLLAPSES the card to the chosen answer, then
 *                                 auto-generates and auto-opens the report.
 *
 * Generation uses the exact path the Reports-tab popup uses
 * (buildReportRequestParams -> fetch-data -> openReportWindow) at default
 * settings (latest business day, USD), opening the report as a native OS
 * popup over the chat. The button then caches the result and re-opens it on
 * later clicks, labelled with report name + client + as-of date.
 */
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Loader2, MessageSquarePlus, Check } from 'lucide-react';
import { useReportWindows } from '../features/reports/windows/ReportWindowsContext';
import { useUI } from '../contexts/UIContext';
import { runSavedReportGeneration } from '../features/reports/lib/generateReport';
import { buildReportRequestParams } from '../features/reports/lib/reportRequest';
import { defaultReportDate, isoToMDY } from '../features/reports/lib/reportDates';
import { groupSigil } from '../features/reports/lib/configLabel';
import { getHandoffSelection, setHandoffSelection } from '../utils/reportHandoffStore';

// Compact market-value label (e.g. "[amount]"), used to disambiguate two clients
// that share a name/codename. Returns "" for missing/zero values.
export const formatMarketValue = (mv) => {
  if (typeof mv !== 'number' || !isFinite(mv) || mv <= 0) return '';
  if (mv >= 1e9) return `$${(mv / 1e9).toFixed(1)}B`;
  if (mv >= 1e6) return `$${(mv / 1e6).toFixed(1)}M`;
  if (mv >= 1e3) return `$${(mv / 1e3).toFixed(0)}K`;
  return `$${mv.toFixed(0)}`;
};

/** A target/candidate that names a group (or family) rather than one account. */
export const isGroupTarget = (t) => t?.kind === 'group' && !!t.group_codename;

/**
 * How a target reads in the UI. Groups carry the "@" display affix the Reports
 * tab uses ("@@" for an aggregate run) - it is never part of the codename value
 * itself; see features/reports/lib/accountSelection.js.
 */
export const targetLabel = (t) => {
  if (isGroupTarget(t)) {
    return `${groupSigil(t.group_mode)}${String(t.group_codename).toUpperCase()}`;
  }
  return t?.client_name || '';
};

const btnClass = (darkMode) =>
  `inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium text-left transition-colors ${
    darkMode
      ? 'border-white/20 text-white hover:bg-white/10'
      : 'border-gray-300 text-gray-900 hover:bg-gray-100'
  }`;

/**
 * The openable report target. `target` carries { report_type, report_title,
 * client_id, client_name, market_value? } for one account, or { kind:'group',
 * group_codename, group_kind } for a group/family. Click generates + opens;
 * later clicks re-open the cached result. `autoStart` kicks generation off on
 * mount (used after a disambiguation choice, so no second click is needed).
 * `onNoClient` handles a target that resolves to neither (opens the picker).
 */
function ReportTargetButton({ target, darkMode, autoStart = false, onNoClient }) {
  const { openReportWindow } = useReportWindows();
  const [status, setStatus] = useState('idle'); // idle | generating | ready | error | blocked
  const cacheRef = useRef(null); // { reportData, requestParams }
  const startedRef = useRef(false); // guards the autoStart effect (StrictMode double-invoke)

  const date = target.date || defaultReportDate(); // ISO — requested date, else latest
  const dateLabel = isoToMDY(date);
  const title = target.report_title || 'report';
  const isGroup = isGroupTarget(target);
  const label = targetLabel(target);
  const who = label ? ` for ${label}` : '';
  const reportName = `${title}${label ? ` — ${label}` : ''}`;
  const mv = formatMarketValue(target.market_value);
  // A group is resolved by its codename; an account by its client id.
  const resolved = isGroup || target.client_id != null;

  const generate = async () => {
    if (startedRef.current) return;
    startedRef.current = true;
    setStatus('generating');
    try {
      // Group targets take the same params the Reports tab's "@CODENAME"
      // selection builds (bare codename + mode); everything else resolves by
      // client id / name.
      const selection = isGroup
        ? {
            group_codename: target.group_codename,
            group_mode: target.group_mode === 'aggregate' ? 'aggregate' : 'sequential',
            portfolioType: 'group',
          }
        : { client_name: target.client_name };
      const requestParams = buildReportRequestParams(
        { date, currency: 'USD', ...selection },
        { reportType: target.report_type, clientId: target.client_id },
      );
      // Opens the native popup synchronously (inside the gesture, when called
      // from handleClick) and fetches inside `produce`; resolves to the full
      // { reportType, reportName, reportData, requestParams } payload.
      const payload = await runSavedReportGeneration({
        reportType: target.report_type,
        requestParams,
        reportName,
        openReportWindow,
      });
      cacheRef.current = { reportData: payload.reportData, requestParams: payload.requestParams || requestParams };
      setStatus('ready');
    } catch (e) {
      startedRef.current = false;
      if (e && e.message === 'POPUP_BLOCKED') {
        // See the autoStart effect below for why this happens and how it recovers.
        setStatus('blocked');
        return;
      }
      console.error('Chat report generation failed', e);
      setStatus('error');
    }
  };

  // Auto-generate once when this target was reached via an explicit choice.
  // autoStart cannot satisfy the browser's user-gesture requirement for
  // window.open; if blocked, generate() falls back to the manual button (the
  // user clicks to open, which is a real gesture) instead of crashing.
  useEffect(() => {
    if (autoStart && resolved) generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart]);

  const handleClick = () => {
    if (status === 'generating') return;

    // Nothing to scope an in-chat generation to (no client, no group) — open the
    // report picker where the user chooses a portfolio.
    if (!resolved) {
      if (typeof onNoClient === 'function') onNoClient(target.report_type);
      return;
    }

    // Re-open the already-generated report (no second round trip).
    if (cacheRef.current) {
      openReportWindow({
        reportName,
        produce: () => ({
          reportType: target.report_type,
          reportName,
          reportData: cacheRef.current.reportData,
          requestParams: cacheRef.current.requestParams,
        }),
      }).catch(() => {});
      return;
    }
    generate();
  };

  const verb =
    status === 'generating' ? 'Generating' : status === 'error' ? 'Retry' : 'Open';

  return (
    <button
      onClick={handleClick}
      disabled={status === 'generating'}
      className={btnClass(darkMode)}
      title={`${title}${who} · ${dateLabel}`}
    >
      {status === 'generating' ? (
        <Loader2 className="w-4 h-4 animate-spin shrink-0" />
      ) : (
        <FileText className="w-4 h-4 shrink-0" />
      )}
      <span>
        {verb} {title}
        {who}
        <span className={darkMode ? 'text-white/50' : 'text-gray-500'}> · {dateLabel}</span>
        {mv ? <span className={darkMode ? 'text-white/50' : 'text-gray-500'}> · {mv}</span> : ''}
      </span>
      {status === 'error' && <span className="text-red-400 text-xs">generation failed</span>}
      {status === 'blocked' && (
        <span className={`text-xs ${darkMode ? 'text-white/50' : 'text-gray-500'}`}>pop-up blocked - click to open</span>
      )}
    </button>
  );
}

/** One selectable row in the choice card. */
function ChoiceRow({ candidate, darkMode, onChoose }) {
  const mv = formatMarketValue(candidate.market_value);
  const dateLabel = isoToMDY(candidate.date || defaultReportDate());
  return (
    <button
      onClick={() => onChoose(candidate)}
      className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors ${
        darkMode ? 'hover:bg-white/5' : 'hover:bg-black/5'
      }`}
    >
      <span
        className={`mt-0.5 w-4 h-4 rounded-full border shrink-0 ${
          darkMode ? 'border-white/40' : 'border-gray-400'
        }`}
      />
      <span className="min-w-0">
        <span className={`block text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          {targetLabel(candidate)}
        </span>
        <span className={`block text-xs ${darkMode ? 'text-white/50' : 'text-gray-500'}`}>
          {isGroupTarget(candidate) ? `${candidate.group_kind === 'family' ? 'Family' : 'Group'} · ` : ''}
          {mv ? `${mv} · ` : ''}as of {dateLabel}
        </span>
      </span>
    </button>
  );
}

/**
 * The full handoff for one assistant message. `onOther(text)` sends a follow-up
 * chat query with the user's typed client name.
 */
export default function ReportHandoff({ reportRequest, reportCandidates, darkMode, onOther, messageId }) {
  const navigate = useNavigate();
  const { closeAIAnalyst } = useUI();
  // Restore a prior selection (survives leaving + returning to the chat); null
  // otherwise. {kind:'candidate',target} | {kind:'other',text}.
  const [choice, setChoice] = useState(() => getHandoffSelection(messageId));
  // Only a FRESH pick auto-generates; a restored selection just shows the
  // open-report button (the report was already generated before).
  const [autoGenerate, setAutoGenerate] = useState(false);
  const [otherActive, setOtherActive] = useState(false);
  const [otherText, setOtherText] = useState('');

  // messageId can arrive after mount (the assistant serverId lands on the final
  // stream event); restore then, without auto-generating.
  useEffect(() => {
    if (choice == null) {
      const saved = getHandoffSelection(messageId);
      if (saved) setChoice(saved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageId]);

  const commitChoice = (c, { autoStart = false } = {}) => {
    setChoice(c);
    setAutoGenerate(autoStart);
    setHandoffSelection(messageId, c);
  };

  const candidates = Array.isArray(reportCandidates) ? reportCandidates : [];
  const hasCandidates = candidates.length > 0;

  if (!hasCandidates && !reportRequest?.report_type) return null;

  // Fallback for a no-client target: leave the analyst and open the report
  // picker so the user can choose a portfolio.
  const openPicker = (reportType) => {
    if (typeof closeAIAnalyst === 'function') closeAIAnalyst();
    navigate(`/reports?report_type=${encodeURIComponent(reportType)}`);
  };

  const submitOther = (e) => {
    e.preventDefault();
    const t = otherText.trim();
    if (!t) return;
    commitChoice({ kind: 'other', text: t });
    if (typeof onOther === 'function') onOther(t);
  };

  const panelClass = `rounded-xl border overflow-hidden divide-y ${
    darkMode ? 'border-white/15 divide-white/10 bg-white/5' : 'border-gray-200 divide-gray-100 bg-black/[0.02]'
  }`;

  // ---- Single resolved client: one openable button. ----
  if (!hasCandidates) {
    return (
      <div className="mt-3 flex flex-col items-start gap-2">
        <ReportTargetButton target={reportRequest} darkMode={darkMode} onNoClient={openPicker} />
      </div>
    );
  }

  // ---- Ambiguous: a choice made -> collapse to the selection + generate. ----
  if (choice) {
    return (
      <div className="mt-3 flex flex-col items-start gap-2">
        <div
          className={`inline-flex items-center gap-2 text-sm ${
            darkMode ? 'text-white/70' : 'text-gray-600'
          }`}
        >
          <Check className="w-4 h-4 text-green-500 shrink-0" />
          {choice.kind === 'candidate' ? targetLabel(choice.target) : `Other: ${choice.text}`}
        </div>
        {choice.kind === 'candidate' && (
          <ReportTargetButton target={choice.target} darkMode={darkMode} autoStart={autoGenerate} onNoClient={openPicker} />
        )}
      </div>
    );
  }

  // ---- Ambiguous: the choice card (options + inline Other). ----
  return (
    <div className="mt-3 w-full max-w-2xl">
      <div className={panelClass}>
        {candidates.map((c, i) => (
          <ChoiceRow
            key={c.client_id ?? c.group_codename ?? i}
            candidate={c}
            darkMode={darkMode}
            onChoose={(cand) => commitChoice({ kind: 'candidate', target: cand }, { autoStart: true })}
          />
        ))}
        {!otherActive ? (
          <button
            onClick={() => setOtherActive(true)}
            className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm transition-colors ${
              darkMode ? 'text-white/80 hover:bg-white/5' : 'text-gray-700 hover:bg-black/5'
            }`}
          >
            <MessageSquarePlus className="w-4 h-4 shrink-0" />
            Other — none of these
          </button>
        ) : (
          <form onSubmit={submitOther} className="flex items-center gap-2 px-4 py-3">
            <MessageSquarePlus className={`w-4 h-4 shrink-0 ${darkMode ? 'text-white/60' : 'text-gray-500'}`} />
            <input
              autoFocus
              value={otherText}
              onChange={(e) => setOtherText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') { setOtherActive(false); setOtherText(''); } }}
              placeholder="Type a client name or codename…"
              className={`flex-1 bg-transparent text-sm outline-none ${
                darkMode ? 'text-white placeholder-white/40' : 'text-gray-900 placeholder-gray-400'
              }`}
            />
            <button
              type="submit"
              disabled={!otherText.trim()}
              className={`px-3 py-1 rounded-md border text-sm ${
                darkMode ? 'border-white/20 text-white hover:bg-white/10' : 'border-gray-300 text-gray-900 hover:bg-gray-100'
              } disabled:opacity-40`}
            >
              Ask
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
